import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { usePackageStore } from '@store'
import { X, Save } from 'lucide-react'
import { Modal, Button, Input, Select, Textarea, Alert, CertificateSelector, FeatureSelector } from '@components/common'
import './PackageForm.css'

/**
 * Package Form Validation Schema
 */
const packageSchema = z.object({
	title: z
		.string()
		.min(3, 'Title must be at least 3 characters')
		.max(100, 'Title cannot exceed 100 characters'),
	slug: z
		.string()
		.min(3, 'Slug must be at least 3 characters')
		.max(100, 'Slug cannot exceed 100 characters')
		.regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens'),
	description: z
		.string()
		.max(1000, 'Description cannot exceed 1000 characters')
		.optional()
		.or(z.literal('')),
	price: z
		.string()
		.min(1, 'Price is required')
		.refine((val) => !isNaN(parseFloat(val)) && parseFloat(val) >= 0, {
			message: 'Price must be a positive number',
		}),
	billing_cycle: z.enum(['monthly', 'yearly'], {
		errorMap: () => ({ message: 'Billing cycle must be monthly or yearly' }),
	}),
	user_limit: z
		.string()
		.min(1, 'User limit is required')
		.refine((val) => !isNaN(parseInt(val)) && parseInt(val) > 0, {
			message: 'User limit must be a positive number',
		}),
	storage_limit: z
		.string()
		.min(1, 'Storage limit is required')
		.refine((val) => !isNaN(parseInt(val)) && parseInt(val) > 0, {
			message: 'Storage limit must be a positive number',
		}),
	trial_days: z
		.string()
		.min(1, 'Trial days is required')
		.refine((val) => !isNaN(parseInt(val)) && parseInt(val) >= 0, {
			message: 'Trial days must be a non-negative number',
		}),
	status: z.enum(['active', 'archived'], {
		errorMap: () => ({ message: 'Status must be active or archived' }),
	}),
	certificates: z.array(z.number()).optional().default([]),
	features: z.array(z.number()).optional().default([]),
})

/**
 * PackageForm Component
 *
 * Universal form for creating and editing subscription packages
 *
 * Features:
 * - React Hook Form for form management
 * - Zod for validation
 * - Auto-generate slug from title
 * - Real-time validation
 * - Loading states
 * - Error handling
 * - Accessible
 *
 * @param {string} mode - 'create' or 'edit'
 * @param {Function} onClose - Close modal callback
 */
const PackageForm = ({ mode = 'create', onClose }) => {
	const { selectedPackage, createPackage, updatePackage } = usePackageStore()

	const [isSubmitting, setIsSubmitting] = useState(false)
	const [submitError, setSubmitError] = useState(null)

	const isEditMode = mode === 'edit'

	// Initialize form with React Hook Form + Zod
	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
		watch,
		reset,
	} = useForm({
		resolver: zodResolver(packageSchema),
		mode: 'onChange',
		defaultValues: {
			title: '',
			slug: '',
			description: '',
			price: '',
			billing_cycle: 'yearly',
			user_limit: '',
			storage_limit: '',
			trial_days: '15',
			status: 'active',
			certificates: [],
			features: [],
		},
	})

	const titleValue = watch('title')
	const certificatesValue = watch('certificates')
	const featuresValue = watch('features')

	// Reset form when selectedPackage changes (for edit mode)
	useEffect(() => {
		if (isEditMode && selectedPackage) {
			const formData = {
				title: selectedPackage.title || '',
				slug: selectedPackage.slug || '',
				description: selectedPackage.description || '',
				price: selectedPackage.price || '',
				billing_cycle: selectedPackage.billing_cycle || 'yearly',
				user_limit: selectedPackage.user_limit?.toString() || '',
				storage_limit: selectedPackage.storage_limit
					? (parseInt(selectedPackage.storage_limit) / (1024 * 1024 * 1024)).toString()
					: '',
				trial_days: selectedPackage.trial_days?.toString() || '15',
				status: selectedPackage.status || 'active',
				certificates: selectedPackage.certificates || [],
				features: Array.isArray(selectedPackage.features)
					? selectedPackage.features
					: [],
			}

			reset(formData)
		}
	}, [selectedPackage, isEditMode, reset])

	// Auto-generate slug from title
	useEffect(() => {
		if (!isEditMode && titleValue) {
			const generatedSlug = titleValue
				.toLowerCase()
				.replace(/[^a-z0-9\s-]/g, '')
				.replace(/\s+/g, '-')
				.replace(/-+/g, '-')
				.trim()

			setValue('slug', generatedSlug)
		}
	}, [titleValue, isEditMode, setValue])

	// Handle form submission
	const onSubmit = async (data) => {
		setIsSubmitting(true)
		setSubmitError(null)

		try {
			// Convert storage from GB to bytes
			const storageInBytes = parseInt(data.storage_limit) * 1024 * 1024 * 1024

			const formData = {
				title: data.title,
				slug: data.slug,
				description: data.description || '',
				price: parseFloat(data.price).toFixed(2),
				billing_cycle: data.billing_cycle,
				user_limit: parseInt(data.user_limit),
				storage_limit: storageInBytes,
				trial_days: parseInt(data.trial_days),
				status: data.status,
				certificates: data.certificates || [],
				features: data.features || [],
			}

			if (isEditMode) {
				await updatePackage(selectedPackage.id, formData)
			} else {
				await createPackage(formData)
			}

			onClose()
		} catch (error) {
			console.error('Form submission error:', error)
			setSubmitError(error.message || 'Failed to save package')
		} finally {
			setIsSubmitting(false)
		}
	}

	const modalTitle = isEditMode ? 'Edit Package' : 'Create New Package'

	const footer = (
		<>
			<Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
				Cancel
			</Button>
			<Button
				variant="primary"
				onClick={handleSubmit(onSubmit)}
				loading={isSubmitting}
				disabled={!isDirty && isEditMode}
			>
				<Save size={18} />
				<span>{isEditMode ? 'Update Package' : 'Create Package'}</span>
			</Button>
		</>
	)

	return (
		<Modal
			isOpen={true}
			onClose={onClose}
			title={modalTitle}
			size="large"
			footer={footer}
			closeOnEscape={false}
			closeOnBackdrop={false}
		>
			<form className="package-form" onSubmit={handleSubmit(onSubmit)}>
				{submitError && (
					<Alert variant="error" message={submitError} dismissible onClose={() => setSubmitError(null)} />
				)}

				{/* Basic Information */}
				<div className="package-form__section">
					<h3 className="package-form__section-title">Basic Information</h3>

					<div className="package-form__grid">
						<Input
							label="Package Title"
							placeholder="e.g., Professional Plan"
							{...register('title')}
							error={errors.title?.message}
							required
							disabled={isSubmitting}
						/>

						<Input
							label="URL Slug"
							placeholder="e.g., professional-plan"
							{...register('slug')}
							error={errors.slug?.message}
							required
							disabled={isSubmitting || isEditMode}
						/>
					</div>

					<div className="package-form__full">
						<Textarea
							label="Description"
							placeholder="Describe the package and its benefits..."
							rows={4}
							maxLength={1000}
							showCount
							{...register('description')}
							error={errors.description?.message}
							disabled={isSubmitting}
						/>
					</div>
				</div>

				{/* Pricing */}
				<div className="package-form__section">
					<h3 className="package-form__section-title">Pricing</h3>

					<div className="package-form__grid">
						<Input
							label="Price"
							type="number"
							step="0.01"
							min="0"
							placeholder="0.00"
							{...register('price')}
							error={errors.price?.message}
							required
							disabled={isSubmitting}
						/>

						<Select
							label="Billing Cycle"
							{...register('billing_cycle')}
							options={[
								{ value: 'monthly', label: 'Monthly' },
								{ value: 'yearly', label: 'Yearly' },
							]}
							error={errors.billing_cycle?.message}
							required
							disabled={isSubmitting}
						/>
					</div>
				</div>

				{/* Limits */}
				<div className="package-form__section">
					<h3 className="package-form__section-title">Limits & Quotas</h3>

					<div className="package-form__grid">
						<Input
							label="User Limit"
							type="number"
							min="1"
							placeholder="e.g., 10"
							{...register('user_limit')}
							error={errors.user_limit?.message}
							required
							disabled={isSubmitting}
						/>

						<Input
							label="Storage Limit (GB)"
							type="number"
							min="1"
							placeholder="e.g., 100"
							{...register('storage_limit')}
							error={errors.storage_limit?.message}
							required
							disabled={isSubmitting}
						/>

						<Input
							label="Trial Days"
							type="number"
							min="0"
							placeholder="e.g., 15"
							{...register('trial_days')}
							error={errors.trial_days?.message}
							required
							disabled={isSubmitting}
						/>

						<Select
							label="Status"
							{...register('status')}
							options={[
								{ value: 'active', label: 'Active' },
								{ value: 'archived', label: 'Archived' },
							]}
							error={errors.status?.message}
							required
							disabled={isSubmitting}
						/>
					</div>
				</div>

				{/* Certificates */}
				<div className="package-form__section">
					<h3 className="package-form__section-title">Certificates</h3>

					<div className="package-form__full">
						<CertificateSelector
							value={certificatesValue}
							onChange={(certificates) => setValue('certificates', certificates, { shouldDirty: true })}
							disabled={isSubmitting}
							error={errors.certificates?.message}
						/>
					</div>
				</div>

				{/* Features */}
				<div className="package-form__section">
					<h3 className="package-form__section-title">Features</h3>

					<div className="package-form__full">
						<FeatureSelector
							value={featuresValue}
							onChange={(features) => setValue('features', features, { shouldDirty: true })}
							disabled={isSubmitting}
							error={errors.features?.message}
						/>
					</div>
				</div>
			</form>
		</Modal>
	)
}

export default PackageForm
